import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useMutation } from "@apollo/client/react";
import { useParams } from "react-router-dom";
import { RESET_PASSWORD } from "../graphql/mutation";
import CustomSnackbar from "../components/Snackbar";
import UnitedGroovesLogo from "../assets/united-grooves-logo.png";
import themestyle from "../theme";
import { MESSAGES, REGEX } from "../utils/const";
import Copyright from "../components/Copyright";
import { PAGE_TITLES } from "../utils/const";
import {
  ResetPasswordResponse,
  ResetPasswordVariables,
} from "../interfaces/auth.interface";

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<"success" | "error">("error");
  const [resetPassword, { loading }] = useMutation<
  ResetPasswordResponse,
  ResetPasswordVariables
>(RESET_PASSWORD);

  useEffect(() => {
    document.title = `${PAGE_TITLES.RESET_PASSWORD} | United Grooves Admin Panel`;
  }, []);

  const validatePassword = (value: string) => {
    if (!value) return MESSAGES.PASS_REQUIRED;
    if (value.length < 8) return MESSAGES.PASSWORD_MIN;
    if (!REGEX.UPPERCASE.test(value)) return MESSAGES.PASSWORD_UPPER;
    if (!REGEX.LOWERCASE.test(value)) return MESSAGES.PASSWORD_LOWER;
    if (!REGEX.NUMBER.test(value)) return MESSAGES.PASSWORD_NUMBER;
    if (!REGEX.SPECIAL_CHAR.test(value)) return MESSAGES.PASSWORD_SPECIAL;
    return "";
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError(validatePassword(value));
    if (confirmPassword && value !== confirmPassword) {
      setConfirmPasswordError(MESSAGES.PASSWORD_NOT_MATCH);
    } else {
      setConfirmPasswordError("");
    }
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setConfirmPasswordError(
      value !== password ? MESSAGES.PASSWORD_NOT_MATCH : ""
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pwdError = validatePassword(password);
    const confirmError =
      password !== confirmPassword ? MESSAGES.PASSWORD_NOT_MATCH : "";
    setPasswordError(pwdError);
    setConfirmPasswordError(confirmError);
    if (pwdError || confirmError || !token) return;
    try {
      const { data } = await resetPassword({
        variables: {
          token,
          newPassword: password,
        },
      });
      if (data?.resetPassword?.success) {
        setSnackbarSeverity("success");
        setSnackbarMessage(data.resetPassword.message);
        setSnackbarOpen(true);
        setTimeout(() => (window.location.href = "/"), 1500);
      } else {
        setSnackbarSeverity("error");
        setSnackbarMessage(data?.resetPassword?.message || "");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error(err);
    }
  };
  const isFormValid =
    password &&
    confirmPassword &&
    !passwordError &&
    !confirmPasswordError;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 4, sm: 3, md: 6 },
          bgcolor: themestyle.colors.white,
          position: "relative",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 420 }}>
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 1.2,
              mb: 3,
            }}
          >
            <Box
              component="img"
              src={UnitedGroovesLogo}
              alt="United Grooves"
              sx={{ width: 22, height: 31 }}
            />
            <Typography
              sx={{
                fontFamily: themestyle.fontFamily.Roboto,
                fontWeight: 500,
                fontSize: "16px",
                letterSpacing: "1px",
                mt: "3px",
                ml: "4px",
              }}
            >
              {MESSAGES.BRAND_NAME}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: themestyle.fontFamily.Roboto,
              fontWeight: 500,
              fontSize: { xs: "30px", sm: "2.2rem", md: "3rem" },
              mb: 1,
              ml: { xs: 0, sm: -1, md: -1 },
            }}
          >
            {MESSAGES.RESET_PASSWORD}
          </Typography>
          <Typography
            sx={{
              color: themestyle.colors.textSecondary,
              fontFamily: themestyle.fontFamily.Roboto,
              fontSize: { xs: "10px", sm: "12px", md: "14px" },
              fontWeight: 400,
              mb: { xs: 1, sm: 2, md: 4 },
              pr: 5,
              ml: { xs: 0, sm: -1, md: -1 },
            }}
          >
            {MESSAGES.RESET_PASSWORD_SUBTITLE}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Box sx={{  mb: 1.5, mt: 3  }}>
              <Typography sx={{ mb: 0.8, fontWeight: 600 , fontSize: { xs: "10px", sm: "0.9rem" },}}>
              {MESSAGES.NEW_PASSWORD}
            </Typography>
            <TextField
              fullWidth
              placeholder={MESSAGES.NEW_PASSWORD}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              error={!!passwordError}
              helperText={passwordError}
              FormHelperTextProps={{
                sx: {
                  fontSize: { xs: "8px", sm: "0.75rem" },
                  mt: "4px",
                  ml: "2px",
                },
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: { xs: "40px", sm: "46px" },
                  "& input": {
                      fontSize: { xs: "10px", sm: "14px" },
                      padding: { xs: "8px 12px", sm: "10px 12px" },
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themestyle.colors.activeGreen,
                    borderWidth: "1px",
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      size="small"
                      sx={{ "& svg": { fontSize: { xs: 18, sm: 20, md: 24, } } }}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography sx={{ mb: 0.8, fontWeight: 600 , fontSize: { xs: "10px", sm: "0.9rem" },}}>
              {MESSAGES.CONFIRM_PASSWORD}
            </Typography>
            <TextField
              fullWidth
              placeholder={MESSAGES.CONFIRM_PASSWORD_PLACEHOLDER}
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
              FormHelperTextProps={{
                sx: {
                  fontSize: { xs: "8px", sm: "0.75rem" },
                  mt: "4px",
                  ml: "2px",
                },
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: { xs: "40px", sm: "46px" },
                  "& input": {
                      fontSize: { xs: "10px", sm: "14px" },
                      padding: { xs: "8px 12px", sm: "10px 12px" },
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themestyle.colors.activeGreen,
                    borderWidth: "1px",
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      size="small"
                      sx={{ "& svg": { fontSize: { xs: 18, sm: 20, md: 24, } } }}
                    >
                      {showConfirmPassword ? (
                        <Visibility />
                      ) : (
                        <VisibilityOff />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            </Box>
            <Button
              type="submit"
              fullWidth
              disabled={!isFormValid || loading}
              sx={{
                width: "100%",
                mt: { md: 1 },
                backgroundColor: themestyle.colors.activeGreen,
                color: themestyle.colors.white,
                fontSize: { xs: "11px", sm: "16px" },
                py: 1.2,
                height: { xs: "38px", sm: "auto" },
                textTransform: "none",
                borderRadius: "5px",
                "&.Mui-disabled": {
                  backgroundColor: themestyle.colors.GRAY_DISABLED,
                  color: themestyle.colors.white,
                },
                "&:hover": {
                  backgroundColor: themestyle.colors.secondary,
                },
              }}
            >
              {MESSAGES.RESET_PASSWORD}
            </Button>
          </form>
          <Box
            sx={{
              mt: { xs: 1.2, sm: 1.8, md: 2.3 },
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily:themestyle.fontFamily.Roboto,
                fontSize: { xs: "10px", sm: "16px" },
                color: themestyle.colors.textSecondary,
                fontWeight:400
              }}
            >
              {MESSAGES.ALREADY_HAVE_ACCOUNT}{" "}
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: "10px", sm: "16px" },
                  fontFamily:themestyle.fontFamily.Roboto,
                  color: themestyle.colors.activeGreen,
                   fontWeight:400,
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
                onClick={() => window.location.href = "/"}
              >
                {MESSAGES.SIGN_IN}
              </Typography>
            </Typography>
          </Box>
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <Copyright />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          bgcolor: themestyle.colors.black,
          position: "relative",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Copyright />
        <Box textAlign="center">
          <Box
            component="img"
            src={UnitedGroovesLogo}
            alt="United Grooves"
            sx={{ width: { xs: 80, md: 200 }, mb: 2 }}
          />
          <Typography
            sx={{
              color: themestyle.colors.white,
              fontSize: { xs: 18, md: 40 },
              letterSpacing: { xs: "2px", md: "12px" },
            }}
          >
            {MESSAGES.BRAND_NAME}
          </Typography>
        </Box>
      </Box>
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};
export default ResetPassword;